import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type AppNotification,
} from "../api/notifications";
import { Button, Card, EmptyState, ErrorState, PageHeader, Spinner } from "../components/ui";

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "baru saja";
  if (mins < 60) return `${mins} menit lalu`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} jam lalu`;
  return `${Math.floor(hours / 24)} hari lalu`;
}

export function NotificationsPage() {
  const queryClient = useQueryClient();
  const notificationsQuery = useQuery({
    queryKey: ["notifications"],
    queryFn: fetchNotifications,
  });

  async function readAll() {
    await markAllNotificationsRead();
    await queryClient.invalidateQueries({ queryKey: ["notifications"] });
  }

  async function readOne(id: string) {
    await markNotificationRead(id);
    await queryClient.invalidateQueries({ queryKey: ["notifications"] });
  }

  if (notificationsQuery.isPending) return <Spinner />;
  if (notificationsQuery.isError || !notificationsQuery.data) {
    return <ErrorState onRetry={() => void notificationsQuery.refetch()} />;
  }

  const { notifications, unread } = notificationsQuery.data;

  return (
    <div>
      <PageHeader
        title="Notifikasi"
        subtitle={unread > 0 ? `${unread} belum dibaca` : "Semua sudah dibaca"}
        action={
          unread > 0 ? (
            <Button variant="outline" onClick={() => void readAll()}>
              Tandai semua dibaca
            </Button>
          ) : undefined
        }
      />
      {notifications.length === 0 ? (
        <EmptyState title="Belum ada notifikasi." hint="Notifikasi booking akan muncul di sini." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {notifications.map((n: AppNotification) => (
            <Card key={n.id} className="list-row">
              <button
                type="button"
                className="list-row"
                style={{
                  width: "100%",
                  textAlign: "left",
                  border: "none",
                  background: "transparent",
                  padding: 0,
                  cursor: n.readAt ? "default" : "pointer",
                }}
                onClick={() => void (n.readAt ? Promise.resolve() : readOne(n.id))}
              >
                <div className="list-row-top">
                  <strong>{n.title}</strong>
                  <span className="muted-line">{relativeTime(n.createdAt)}</span>
                </div>
                {n.body ? (
                  <p className="list-row-main" style={{ marginBottom: 0 }}>
                    {n.body}
                  </p>
                ) : null}
                {!n.readAt ? <span className="status-pill tone-teal">Baru</span> : null}
              </button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
